if (!customElements.get('nd-length-selector')) {
  customElements.define(
    'nd-length-selector',
    class NdLengthSelector extends HTMLElement {
      connectedCallback() {
        this.lengthInput = this.querySelector('[data-length-input]');
        if (!this.lengthInput) return;

        // When one inventory unit covers more than 1 unit of measure, the
        // quantity is carried by a hidden input; otherwise length == quantity.
        this.quantityInput = this.querySelector('[data-quantity-input]') || this.lengthInput;
        this.presetButtons = Array.from(this.querySelectorAll('[data-length]'));
        this.totalElement = this.querySelector('[data-length-total]');
        this.breakdownElement = this.querySelector('[data-length-breakdown]');
        this.unitLabelElement = this.querySelector('[data-unit-label]');

        this.price = parseInt(this.dataset.price, 10) || 0;
        this.min = parseFloat(this.dataset.min) || 1;
        this.max = parseFloat(this.dataset.max) || null;
        this.step = parseFloat(this.dataset.step) || 1;
        this.unit = this.dataset.unit || 'm';
        this.unitsPerItem = parseFloat(this.dataset.unitsPerItem) || 1;
        this.unitPrice = parseInt(this.dataset.unitPrice, 10) || this.price;
        this.refUnit = this.dataset.refUnit || this.unit;
        this.refValue = parseFloat(this.dataset.refValue) || 1;

        // Per-variant min/max lengths resolved server-side from the
        // b2b.min_order_qty / b2b.max_order_qty variant metafields.
        this.variantRules = {};
        const rulesElement = this.querySelector('[data-variant-rules]');
        if (rulesElement) {
          try {
            this.variantRules = JSON.parse(rulesElement.textContent);
          } catch (error) {
            // Malformed rules fall back to the block-level min/max.
          }
        }

        this.presetButtons.forEach((button) =>
          button.addEventListener('click', () => {
            this.lengthInput.value = button.dataset.length;
            this.onLengthChange();
          })
        );
        this.lengthInput.addEventListener('change', this.onLengthChange.bind(this));
        this.lengthInput.addEventListener('input', this.onLengthPreview.bind(this));

        // Sanitize the quantity before product-form.js builds its FormData on submit.
        this.onFormSubmit = (event) => {
          const formId = this.quantityInput.getAttribute('form');
          if (formId && event.target.id === formId) this.onLengthChange();
        };
        document.addEventListener('submit', this.onFormSubmit, true);

        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId !== this.dataset.sectionId) return;
            this.onVariantChange(event.data.variant);
          });
        }

        this.onLengthChange();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        if (this.onFormSubmit) document.removeEventListener('submit', this.onFormSubmit, true);
      }

      onVariantChange(variant) {
        if (!variant) return;
        if (typeof variant.price === 'number') this.price = variant.price;

        const measurement = variant.unit_price_measurement;
        if (measurement && measurement.quantity_unit) {
          this.unit = measurement.quantity_unit;
          if (measurement.reference_unit) this.refUnit = measurement.reference_unit;
          if (measurement.reference_value > 0) this.refValue = measurement.reference_value;
          // Only trust a changed units-per-item where the hidden quantity field
          // exists to carry the conversion; in 1:1 mode the length input is the
          // quantity input itself.
          if (measurement.quantity_value > 0 && this.quantityInput !== this.lengthInput) {
            this.unitsPerItem = measurement.quantity_value;
          }
          this.unitPrice = typeof variant.unit_price === 'number' ? variant.unit_price : this.price;
        } else {
          this.unitPrice = this.price;
        }

        const rules = this.variantRules[variant.id];
        if (rules) {
          if (rules.min > 0) this.min = rules.min;
          if (rules.max > 0) this.max = rules.max;
          this.lengthInput.min = this.min;
          if (this.max) this.lengthInput.max = this.max;
          this.updatePresetVisibility();
        }

        if (this.unitLabelElement) this.unitLabelElement.textContent = this.unit;
        this.onLengthChange();
      }

      updatePresetVisibility() {
        this.presetButtons.forEach((button) => {
          const length = parseFloat(button.dataset.length);
          button.hidden = length < this.min || (this.max !== null && length > this.max);
        });
      }

      // Clamp a requested length to min/max/step, then round up to a whole
      // number of inventory units so the customer never gets less than asked.
      computeState(rawLength) {
        let length = rawLength;
        if (isNaN(length) || length <= 0) length = this.min;
        length = Math.max(this.min, length);
        length = Math.ceil(length / this.step) * this.step;
        let quantity = Math.max(1, Math.ceil(length / this.unitsPerItem));
        if (this.max) quantity = Math.min(quantity, Math.max(1, Math.floor(this.max / this.unitsPerItem)));
        length = Math.round(quantity * this.unitsPerItem * 1000) / 1000;
        return { length, quantity };
      }

      onLengthChange() {
        const state = this.computeState(parseFloat(this.lengthInput.value));
        this.lengthInput.value = state.length;
        this.quantityInput.value = this.quantityInput === this.lengthInput ? state.length : state.quantity;
        this.render(state);
      }

      onLengthPreview() {
        const raw = parseFloat(this.lengthInput.value);
        if (isNaN(raw) || raw <= 0) return;
        this.render(this.computeState(raw));
      }

      render(state) {
        this.presetButtons.forEach((button) =>
          button.classList.toggle('is-active', parseFloat(button.dataset.length) === state.length)
        );
        if (this.totalElement) this.totalElement.textContent = this.formatMoney(state.quantity * this.price);
        if (this.breakdownElement) {
          const reference = `${this.refValue !== 1 ? this.refValue : ''}${this.refUnit}`;
          this.breakdownElement.textContent = `${state.length.toLocaleString()}${this.unit} × ${this.formatMoney(
            this.unitPrice
          )}/${reference}`;
        }
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '£{{amount}}';
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;
        const match = format.match(placeholder);
        const value = cents / 100;
        let formatted;
        switch (match ? match[1] : 'amount') {
          case 'amount_no_decimals':
            formatted = Math.round(value).toLocaleString('en-GB');
            break;
          case 'amount_with_comma_separator':
            formatted = value
              .toFixed(2)
              .replace('.', ',')
              .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            break;
          case 'amount_no_decimals_with_comma_separator':
            formatted = Math.round(value)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            break;
          default:
            formatted = value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
        return match ? format.replace(placeholder, formatted) : formatted;
      }
    }
  );
}
